import type { ApiResponse, ApiError } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getAuthToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    };

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          message: `HTTP ${response.status}: ${response.statusText}`,
          status: response.status,
        })) as ApiError;
        
        // Handle 401 Unauthorized - token expired or invalid
        if (response.status === 401) {
          this.handleUnauthorized();
        }
        
        throw error;
      }

      const data: ApiResponse<T> = await response.json();

      if (!data.success && data.error) {
        throw {
          message: data.error,
          status: response.status,
        } as ApiError;
      }

      return data.data as T;
    } catch (error) {
      if (error instanceof Error) {
        throw {
          message: error.message,
          status: 500,
        } as ApiError;
      }
      throw error;
    }
  }

  private getAuthToken(): string | null {
    try {
      const auth = localStorage.getItem('adwstats-auth');
      if (auth) {
        const parsed = JSON.parse(auth);
        return parsed.state?.token || null;
      }
    } catch {
      return null;
    }
    return null;
  }

  private handleUnauthorized(): void {
    // Clear auth state from localStorage
    localStorage.removeItem('adwstats-auth');
    
    // Redirect to login page
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }

  // Convenience methods
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<any> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const response = await fetch(`${this.baseUrl}/auth/login`, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`,
        status: response.status,
      }));
      throw error;
    }

    return await response.json();
  }

  async logout() {
    return this.request('/auth/logout', {
      method: 'POST',
    });
  }

  async refreshToken() {
    return this.request('/auth/refresh', {
      method: 'POST',
    });
  }

  // User endpoints
  async getMe() {
    return this.request('/users/me');
  }

  async updateProfile(data: any) {
    return this.request('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Networks
  async getNetworks() {
    return this.request('/networks');
  }

  async getNetwork(id: string) {
    return this.request(`/networks/${id}`);
  }

  async createNetwork(data: any) {
    return this.request('/networks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateNetwork(id: string, data: any) {
    return this.request(`/networks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteNetwork(id: string) {
    return this.request(`/networks/${id}`, {
      method: 'DELETE',
    });
  }

  // Players
  async getPlayers(params?: Record<string, any>) {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`/players${query}`);
  }

  async getPlayer(id: string) {
    return this.request(`/players/${id}`);
  }

  // Campaigns
  async getCampaigns(params?: Record<string, any>) {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`/campaigns${query}`);
  }

  async getCampaign(id: string) {
    return this.request(`/campaigns/${id}`);
  }

  async createCampaign(data: any) {
    return this.request('/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCampaign(id: string, data: any) {
    return this.request(`/campaigns/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteCampaign(id: string) {
    return this.request(`/campaigns/${id}`, {
      method: 'DELETE',
    });
  }

  // Statistics
  async getStatistics(params?: Record<string, any>) {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`/statistics${query}`);
  }

  async getDailyStatistics(params?: Record<string, any>) {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`/statistics/daily${query}`);
  }

  // Payments
  async getPayments(params?: Record<string, any>) {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`/payments${query}`);
  }

  async requestPayment(data: any) {
    return this.request('/payments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Reports
  async getReports() {
    return this.request('/reports');
  }

  async generateReport(data: any) {
    return this.request('/reports', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async downloadReport(id: string) {
    return this.request(`/reports/${id}/download`);
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
export default apiClient;
